import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('shine_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      localStorage.removeItem('shine_token');
      localStorage.removeItem('shine_user');
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

export const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = reader.result || '';
    // strip data URL prefix
    const idx = String(result).indexOf('base64,');
    resolve(idx >= 0 ? String(result).slice(idx + 7) : String(result));
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

/**
 * Fetch a protected PDF with the logged-in JWT and open or download it from
 * a Blob URL. Bypasses the browser's native cross-tab navigation (which does
 * not include our Authorization header) and respects the backend's 403/404
 * permission rules.
 *
 *   openProposalPdf(id, { mode: 'open' | 'download', filename })
 */
export async function openProposalPdf(id, { mode = 'open', filename, onError } = {}) {
  try {
    const resp = await api.get(`/proposals/${id}/pdf`, { responseType: 'blob' });
    const blob = new Blob([resp.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    if (mode === 'download') {
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `proposal_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } else {
      window.open(url, '_blank', 'noopener');
    }
    // Revoke after a bit so the new tab has time to render.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return { ok: true };
  } catch (e) {
    // Axios turns the error body into a Blob when responseType=blob — unwrap
    // it so we can show a useful message.
    let msg = 'Could not open PDF';
    const status = e?.response?.status;
    try {
      const txt = await e?.response?.data?.text?.();
      if (txt) {
        const parsed = JSON.parse(txt);
        msg = parsed.detail || parsed.message || msg;
      }
    } catch {}
    if (status === 403) msg = msg || "You don't have permission to view this proposal's PDF.";
    if (status === 404) msg = 'Proposal not found.';
    if (status === 401) msg = 'Your session has expired. Please sign in again.';
    if (onError) onError(msg, status);
    else {
      // Side-effect-free fallback: throw so caller can toast it.
      throw new Error(msg);
    }
    return { ok: false, status, message: msg };
  }
}


/**
 * Fetch the single-label PDF for an asset and open it in a new tab.
 * Uses the same auth-blob pattern as proposals to carry the JWT.
 */
export async function openAssetLabelPdf(assetId, { onError } = {}) {
  try {
    const resp = await api.get(`/assets/${assetId}/label.pdf`, { responseType: 'blob' });
    const blob = new Blob([resp.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return { ok: true };
  } catch (e) {
    let msg = 'Could not open label';
    const status = e?.response?.status;
    try {
      const txt = await e?.response?.data?.text?.();
      if (txt) {
        const parsed = JSON.parse(txt);
        msg = parsed.detail || parsed.message || msg;
      }
    } catch {}
    if (status === 403) msg = msg || "You don't have permission to print this label.";
    if (status === 404) msg = 'Asset not found.';
    if (onError) onError(msg, status);
    else throw new Error(msg);
    return { ok: false, status, message: msg };
  }
}
